import { useDidUpdateEffect } from '@openeducation/oe-rn-hooks';
import React, { memo, useState } from 'react';
import type { LayoutChangeEvent } from 'react-native';

import { Skeleton, OEText } from '../../atoms';
import type { OETextProps } from '../../atoms/oe-text/oe-text';
import { mb } from '../../style-utils';

const OETEXT_DEFAULT_LINE_HEIGHT = 21;

export type TextWithLoadingProps = OETextProps & {
  isLoading?: boolean;
  originalText?: string | null;
  newText?: string | null;
  skeletonBackgroundColor?: string;
  skeletonGradientColor?: string;
};

const _TextWithLoading = ({
  isLoading = false,
  originalText,
  newText,
  lineHeight = OETEXT_DEFAULT_LINE_HEIGHT,
  skeletonBackgroundColor,
  skeletonGradientColor,
  ...props
}: TextWithLoadingProps) => {
  const [numberOfLines, setNumberOfLines] = useState(0);
  const animationDuration = 1200;
  const [displayText, setDisplayText] = useState<React.ReactNode>(
    newText || originalText
  );
  const [textChanging, setTextChanging] = useState(false);

  useDidUpdateEffect(() => {
    if (!newText) {
      setDisplayText(originalText);
      return;
    }
    setTextChanging(true);

    const timeout = setTimeout(() => {
      setDisplayText(newText || originalText);
      setTextChanging(false);
    }, animationDuration / 2);

    return () => {
      clearTimeout(timeout);
    };
  }, [newText, originalText]);

  const onTextLayout = (e: LayoutChangeEvent) => {
    const lines = Math.trunc(e.nativeEvent.layout.height / lineHeight);
    setNumberOfLines(lines);
  };

  if (isLoading || textChanging) {
    return (
      <>
        {Array.from(Array(numberOfLines).keys()).map((id) => (
          <Skeleton
            key={id}
            style={mb(4)}
            height={lineHeight - 4}
            backgroundColor={skeletonBackgroundColor}
            gradientColor={skeletonGradientColor}
          />
        ))}
      </>
    );
  }

  if (displayText) {
    return (
      <OEText onLayout={onTextLayout} lineHeight={lineHeight} {...props}>
        {displayText}
      </OEText>
    );
  }

  return null;
};

export const TextWithLoading = memo(_TextWithLoading);
